/**
 * Service de logging centralisé pour les erreurs
 * Permet de logger les erreurs de manière structurée et de les envoyer à un service de monitoring
 */

// Sauvegarder les méthodes console originales AVANT toute interception
const originalConsoleError = console.error;
const originalConsoleWarn = console.warn;
const originalConsoleLog = console.log;

class ErrorLogger {
  constructor() {
    this.errors = [];
    this.maxErrors = 100; // Limite pour éviter la surcharge mémoire
  }

  /**
   * Log une erreur avec contexte
   * @param {Error|string} error - L'erreur à logger
   * @param {Object} context - Contexte additionnel (component, user, etc.)
   * @param {string} level - Niveau de sévérité (error, warning, info)
   */
  log(error, context = {}, level = 'error') {
    const errorEntry = {
      timestamp: new Date().toISOString(),
      level,
      error: error instanceof Error ? {
        name: error.name,
        message: error.message,
        stack: error.stack,
        cause: error.cause
      } : error,
      context: {
        userAgent: navigator.userAgent,
        url: window.location.href,
        user: this.getCurrentUser(),
        ...context
      }
    };

    // Ajouter à la liste locale
    this.errors.push(errorEntry);
    if (this.errors.length > this.maxErrors) {
      this.errors.shift(); // Supprimer le plus ancien
    }

    // IMPORTANT: Utiliser les méthodes console ORIGINALES pour éviter la boucle infinie
    const logMethod = level === 'error' ? originalConsoleError :
                     level === 'warning' ? originalConsoleWarn : originalConsoleLog;

    logMethod.call(console, `[${level.toUpperCase()}] ${errorEntry.timestamp}:`, errorEntry);

    // Ici on pourrait envoyer à un service externe comme Sentry, LogRocket, etc.
    // this.sendToMonitoringService(errorEntry);
  }

  /**
   * Log une erreur d'API
   * @param {Object} error - Erreur axios/response
   * @param {Object} context - Contexte additionnel
   */
  logApiError(error, context = {}) {
    const apiContext = {
      ...context,
      type: 'api',
      status: error.response?.status,
      statusText: error.response?.statusText,
      url: error.config?.url,
      method: error.config?.method,
      data: error.config?.data,
      responseData: error.response?.data
    };

    this.log(error, apiContext, 'error');
  }

  /**
   * Log une erreur React
   * @param {Error} error - Erreur React
   * @param {Object} errorInfo - Info d'erreur React
   * @param {Object} context - Contexte additionnel
   */
  logReactError(error, errorInfo, context = {}) {
    const reactContext = {
      ...context,
      type: 'react',
      componentStack: errorInfo.componentStack,
      errorBoundary: true
    };

    this.log(error, reactContext, 'error');
  }

  /**
   * Récupère l'utilisateur actuel depuis le contexte ou localStorage
   */
  getCurrentUser() {
    try {
      const user = localStorage.getItem('user');
      return user ? JSON.parse(user) : null;
    } catch {
      return null;
    }
  }

  /**
   * Exporte les erreurs pour debug
   */
  exportErrors() {
    return [...this.errors];
  }

  /**
   * Vide le log des erreurs
   */
  clearErrors() {
    this.errors = [];
  }

  /**
   * Envoie les erreurs à un service de monitoring (à implémenter)
   */
  sendToMonitoringService(errorEntry) {
    // TODO: Implémenter l'envoi à Sentry, LogRocket, ou autre service
    // Exemple avec Sentry:
    // if (window.Sentry) {
    //   window.Sentry.captureException(errorEntry.error, {
    //     contexts: { custom: errorEntry.context }
    //   });
    // }
  }
}

// Instance singleton
const errorLogger = new ErrorLogger();

export default errorLogger;